// src/services/credentialViewService.ts
// ─────────────────────────────────────────────────────────────────
// Credential View Service — Phase 4 (Mock)
//
// Phase 4 additions over Phase 1:
//   • checkCredentialExpiryAndNotify() — scans active crew, fires
//     CREDENTIAL_EXPIRY push notifications (4D-03)
//   • getCrewCredentialsSummary()      — richer summary with days
//     countdown and issue flags per type
//   • getExpiringSoonByJob()           — for the Weekly Roster
//     credential warning badge (4D-04)
//
// At beta: replace function bodies with fetch('/api/credentials/...')
// ─────────────────────────────────────────────────────────────────

import {
  MOCK_WHS_CREDENTIALS,
  MOCK_SITE_INDUCTIONS,
  MOCK_ROSTER_NOTIFICATIONS,
  computeWHSCompliance,
  getCredentialStatus,
  CREDENTIAL_META,
  CREDENTIAL_STATUS_META,
  type WHSCredential,
  type SiteInduction,
} from '../utils/mockDataMobile'
import { addCrewNotification } from '../utils/mockDataNotificationBus'

// Reference date for expiry calculations (mock environment)
const TODAY = '2026-07-11'

function daysUntilExpiry(expiryDate: string | null): number | null {
  if (!expiryDate) return null
  return Math.ceil(
    (new Date(expiryDate).getTime() - new Date(TODAY).getTime()) / 86_400_000
  )
}

// ── Enhanced credential types ──────────────────────────────────────
export interface EnrichedCredential extends WHSCredential {
  statusLabel:     string
  statusBg:        string
  statusText:      string
  typeLabel:       string
  daysUntilExpiry: number | null
  isExpiringSoon:  boolean    // < 30 days
  isExpired:       boolean
  isMissing:       boolean
}

export interface CrewCredentialSummary {
  employeeId:       string
  employeeName:     string
  role:             string
  jobId:            string
  jobNumber:        string
  credentials:      EnrichedCredential[]
  inductions:       SiteInduction[]
  complianceStatus: 'COMPLIANT' | 'INDUCTION_PENDING' | 'INCOMPLETE' | 'EXPIRED'
  complianceIssues: string[]
  hasExpiringSoon:  boolean
  hasExpired:       boolean
  inductionComplete:boolean
}

export interface CredentialExpirySummary {
  employeeId:   string
  employeeName: string
  jobId:        string
  jobNumber:    string
  credentialType:string
  typeLabel:    string
  expiryDate:   string
  daysUntil:    number
}

function enrichCredential(c: WHSCredential): EnrichedCredential {
  const meta      = CREDENTIAL_STATUS_META[c.status]
  const typeMeta  = CREDENTIAL_META[c.type]
  const days      = daysUntilExpiry(c.expiryDate)
  const isSoon    = days !== null && days > 0 && days <= 30
  const isExpired = days !== null && days <= 0

  return {
    ...c,
    statusLabel:     meta.label,
    statusBg:        meta.bg,
    statusText:      meta.text,
    typeLabel:       typeMeta.label,
    daysUntilExpiry: days,
    isExpiringSoon:  isSoon,
    isExpired,
    isMissing:       (c.status as any) === 'MISSING',
  }
}

// ── Build summary per employee + job ─────────────────────────────────
function buildSummary(
  record: typeof MOCK_ROSTER_NOTIFICATIONS[0],
  jobIds?: string[],
): CrewCredentialSummary {
  const empCreds = MOCK_WHS_CREDENTIALS
    .filter(c => c.employeeId === record.employeeId)
    .map(c => enrichCredential({
      ...c,
      status: getCredentialStatus(c.expiryDate),
    }))

  const empInductions = MOCK_SITE_INDUCTIONS.filter(
    i => i.employeeId === record.employeeId &&
         (!jobIds?.length || jobIds.includes(i.jobId)) &&
         i.jobId === record.jobId
  )

  const { status, issues } = computeWHSCompliance(record.employeeId, record.jobId)
  const hasExpiringSoon = empCreds.some(c => c.isExpiringSoon)
  const hasExpired      = empCreds.some(c => c.isExpired || c.isMissing)
  const inductionComplete = empInductions.some(i => (i as any).status === 'COMPLETED')

  return {
    employeeId:       record.employeeId,
    employeeName:     record.employeeName,
    role:             record.role,
    jobId:            record.jobId,
    jobNumber:        record.jobNumber,
    credentials:      empCreds,
    inductions:       empInductions,
    complianceStatus: status,
    complianceIssues: issues,
    hasExpiringSoon,
    hasExpired,
    inductionComplete,
  }
}

// ── Exported service functions ─────────────────────────────────────

/** Credential summaries for crew currently allocated to active jobs. */
export async function getCrewCredentials(jobIds?: string[]): Promise<CrewCredentialSummary[]> {
  // Phase 6: GET /api/credentials/view?jobIds=...
  const activeRecords = MOCK_ROSTER_NOTIFICATIONS.filter(
    n => n.status !== 'COMPLETED' && (!jobIds?.length || jobIds.includes(n.jobId))
  )

  const summaries = activeRecords.map(r => buildSummary(r, jobIds))

  // Deduplicate by employeeId + jobId
  const seen = new Set<string>()
  return Promise.resolve(summaries.filter(s => {
    const key = `${s.employeeId}:${s.jobId}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  }))
}

/** Induction status for a single employee on a specific job. */
export async function getInductionStatus(
  employeeId: string,
  jobId:       string,
): Promise<{ inducted: boolean; record: SiteInduction | null }> {
  // Phase 6: GET /api/credentials/induction?employeeId=...&jobId=...
  const record = MOCK_SITE_INDUCTIONS.find(
    i => i.employeeId === employeeId && i.jobId === jobId
  ) ?? null
  return Promise.resolve({ inducted: !!record && (record as any).status === 'COMPLETED', record })
}

/**
 * 4D-03 — Check credential expiry for all active crew and dispatch
 * push notifications for any expiring within 30 days or already expired.
 * Returns the list of crew with expiry issues (for the Credentials sub-tab badge).
 */
export async function checkCredentialExpiryAndNotify(
  pmEmployeeId: string,
): Promise<CredentialExpirySummary[]> {
  // Phase 6: POST /api/credentials/check-expiry (server-side scheduled job at beta)
  const summaries = await getCrewCredentials()
  const issues: CredentialExpirySummary[] = []

  for (const s of summaries) {
    for (const c of s.credentials) {
      if (!c.isExpiringSoon && !c.isExpired) continue
      if (c.daysUntilExpiry === null) continue

      issues.push({
        employeeId:    s.employeeId,
        employeeName:  s.employeeName,
        jobId:         s.jobId,
        jobNumber:     s.jobNumber,
        credentialType:c.type,
        typeLabel:     c.typeLabel,
        expiryDate:    c.expiryDate!,
        daysUntil:     c.daysUntilExpiry,
      })

      // Push notification to the employee
      addCrewNotification({
        type:          'CREDENTIAL_EXPIRY',
        priority:      c.isExpired ? 'CRITICAL' : c.daysUntilExpiry <= 7 ? 'HIGH' : 'MEDIUM',
        employeeIds:   [s.employeeId, pmEmployeeId],
        jobIds:        [s.jobId],
        heading:       c.isExpired
          ? `EXPIRED credential — ${c.typeLabel} — action required`
          : `Credential expiring in ${c.daysUntilExpiry} day${c.daysUntilExpiry !== 1 ? 's' : ''} — ${c.typeLabel}`,
        body:          c.isExpired
          ? `Your ${c.typeLabel} expired on ${c.expiryDate}. You must not perform work requiring this credential until it is renewed. Contact your PM immediately.`
          : `Your ${c.typeLabel} expires on ${c.expiryDate} (${c.daysUntilExpiry} days). Please arrange renewal before this date.`,
        requiredAction:c.isExpired
          ? 'Contact your PM and arrange renewal before your next shift.'
          : 'Book renewal now. Do not wait until the expiry date.',
        sourceModule:  'credentialViewService',
        sourceRecordId:`${s.employeeId}:${c.type}`,
        requiresAck:   c.isExpired,
        payload:       { credentialType: c.type, typeLabel: c.typeLabel, expiryDate: c.expiryDate, daysUntil: c.daysUntilExpiry },
      })
    }
  }

  return Promise.resolve(issues)
}

/**
 * 4D-04 — Get employees with expiring/expired credentials for a set of jobs.
 * Used by the Weekly Roster grid to show credential warning badges on employee rows.
 */
export async function getExpiringSoonByJob(jobIds: string[]): Promise<Set<string>> {
  // Phase 6: GET /api/credentials/expiring-soon?jobIds=...
  const summaries = await getCrewCredentials(jobIds)
  const empIds = new Set<string>()
  for (const s of summaries) {
    if (s.hasExpiringSoon || s.hasExpired) empIds.add(s.employeeId)
  }
  return Promise.resolve(empIds)
}

/**
 * Send a manual expiry reminder to a specific crew member.
 * Called from the Credentials sub-tab "Send Reminder" button.
 */
export async function sendExpiryReminder(
  employeeId:   string,
  employeeName: string,
  jobId:        string,
  credentialType: string,
  typeLabel:    string,
  expiryDate:   string,
  daysUntil:    number,
): Promise<boolean> {
  // Phase 6: POST /api/credentials/send-reminder
  addCrewNotification({
    type:          'CREDENTIAL_EXPIRY',
    priority:      daysUntil <= 0 ? 'CRITICAL' : daysUntil <= 7 ? 'HIGH' : 'MEDIUM',
    employeeIds:   [employeeId],
    jobIds:        [jobId],
    heading:       daysUntil <= 0
      ? `REMINDER: Your ${typeLabel} has expired`
      : `REMINDER: ${typeLabel} expires in ${daysUntil} day${daysUntil !== 1 ? 's' : ''}`,
    body:          daysUntil <= 0
      ? `Your ${typeLabel} expired on ${expiryDate}. You cannot perform work requiring this credential. Please arrange renewal urgently.`
      : `Reminder: your ${typeLabel} expires on ${expiryDate}. Please arrange renewal before this date.`,
    requiredAction:'Contact your training provider or licensing body to arrange renewal.',
    sourceModule:  'credentialViewService (manual reminder)',
    sourceRecordId:`${employeeId}:${credentialType}`,
    requiresAck:   false,
    payload:       { credentialType, typeLabel, expiryDate, daysUntil },
  })
  return Promise.resolve(true)
}

// Re-export metadata
export { CREDENTIAL_META, CREDENTIAL_STATUS_META }
